import React from 'react';
import { ActionIcon, Checkbox, Indicator, Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faPen as faPenDef,
  faFileLines as faFileLinesDef,
  faLayerGroup as faLayerGroupDef,
  faBan as faBanDef,
  faCircleCheck as faCircleCheckDef,
} from '@fortawesome/free-solid-svg-icons';
import type { ProductView } from '../../../../../services/products/products.service';
import { formatMoney } from '../../../../../utils/money';
import classes from '../shared/settings-shared.module.scss';
import bulkClasses from '../shared/product-settings.module.scss';

const faPen = faPenDef as IconProp;
const faFileLines = faFileLinesDef as IconProp;
const faLayerGroup = faLayerGroupDef as IconProp;
const faBan = faBanDef as IconProp;
const faCircleCheck = faCircleCheckDef as IconProp;

export type ProductSettingsRowProps = {
  product: ProductView;
  categoryName: string;
  printerNamesText: string;
  hasPrinters: boolean;
  variantComboCount: number;
  isSelected: boolean;
  isTogglingProductStatus: boolean;
  isKdsUpdating: boolean;
  reorderMode: boolean;
  cardTapSelect?: boolean;
  rowRef?: (element: HTMLElement | null) => void;
  rowIndex?: number;
  rowStyle?: React.CSSProperties;
  dragHandle?: React.ReactNode;
  onEdit: (product: ProductView) => void;
  onEditDescription: (product: ProductView) => void;
  onOpenOptions: (product: ProductView) => void;
  onDeactivate: (productId: number) => void;
  onActivate: (productId: number) => void;
  onToggleKds: (productId: number, next: boolean) => void;
  onToggleSelect: (productId: number) => void;
};

// Architectural summary: presentational product row; no dnd hooks (those live in the sortable wrapper).
const ProductSettingsRowComponent: React.FC<ProductSettingsRowProps> = ({
  product,
  categoryName,
  printerNamesText,
  hasPrinters,
  variantComboCount,
  isSelected,
  isTogglingProductStatus,
  isKdsUpdating,
  reorderMode,
  cardTapSelect = false,
  rowRef,
  rowStyle,
  dragHandle,
  onEdit,
  onEditDescription,
  onOpenOptions,
  onDeactivate,
  onActivate,
  onToggleKds,
  onToggleSelect,
  rowIndex,
}) => (
  <tr
    ref={rowRef}
    data-index={rowIndex}
    style={rowStyle}
    data-testid={`product-settings-row-${product.id}`}
    aria-selected={cardTapSelect ? isSelected : undefined}
    onClick={cardTapSelect ? () => onToggleSelect(product.id) : undefined}
    className={`${isSelected && !reorderMode ? bulkClasses.cardSelected : ''} ${reorderMode ? bulkClasses.reorderRow : ''}`}
  >
    <td className={bulkClasses.dragCell} data-label="">
      {reorderMode ? dragHandle : null}
    </td>
    <td
      className={bulkClasses.selectCell}
      data-label="בחר"
      onClick={(event) => event.stopPropagation()}
    >
      {reorderMode ? null : (
        <Checkbox
          data-testid={`product-settings-select-${product.id}`}
          aria-label={`בחר ${product.name}`}
          checked={isSelected}
          onChange={() => onToggleSelect(product.id)}
        />
      )}
    </td>
    <td data-label="שם">{product.name}</td>
    <td data-label="מחיר">{formatMoney(product.price)}</td>
    <td data-label="קטגוריה">{categoryName}</td>
    <td data-label="תמונה">
      {product.imageUrl ? (
        <img src={product.imageUrl} alt={product.name} className={classes.thumb} loading="lazy" />
      ) : (
        <span className={classes.thumbPlaceholder}>ללא</span>
      )}
    </td>
    <td data-label="סטטוס" onClick={(event) => event.stopPropagation()}>
      <span className={product.isActive ? classes.statusActive : classes.statusInactive}>
        {product.isActive ? 'פעיל' : 'לא פעיל'}
      </span>
    </td>
    <td data-label="מדפסות" className={hasPrinters ? '' : bulkClasses.emptyOnMobile}>
      {printerNamesText}
    </td>
    <td data-label="KDS" onClick={(event) => event.stopPropagation()}>
      {reorderMode ? null : (
        <Checkbox
          data-testid={`product-settings-kds-${product.id}`}
          aria-label="KDS"
          checked={product.kds}
          onChange={(event) => onToggleKds(product.id, event.currentTarget.checked)}
          disabled={isKdsUpdating || isTogglingProductStatus}
        />
      )}
    </td>
    <td className={bulkClasses.actionsCellTd} onClick={(event) => event.stopPropagation()}>
      {reorderMode ? null : (
        <div className={`${classes.actionsCell} ${bulkClasses.productActionsCell}`}>
          <Tooltip label="ערוך" withArrow>
            <ActionIcon
              aria-label="ערוך"
              data-testid={`product-settings-edit-${product.id}`}
              variant="subtle"
              color="gray"
              radius="md"
              size="lg"
              onClick={() => onEdit(product)}
            >
              <FontAwesomeIcon icon={faPen} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="תיאור" withArrow>
            <ActionIcon
              aria-label="תיאור"
              data-testid={`product-settings-description-${product.id}`}
              variant="subtle"
              color="gray"
              radius="md"
              size="lg"
              onClick={() => onEditDescription(product)}
            >
              <FontAwesomeIcon icon={faFileLines} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="וריאציות וקומבו" withArrow>
            <Indicator label={variantComboCount} disabled={!variantComboCount} color="blue" size={18} offset={4}>
              <ActionIcon
                aria-label="וריאציות וקומבו"
                data-testid={`product-settings-options-${product.id}`}
                variant="subtle"
                color="blue"
                radius="md"
                size="lg"
                onClick={() => onOpenOptions(product)}
              >
                <FontAwesomeIcon icon={faLayerGroup} />
              </ActionIcon>
            </Indicator>
          </Tooltip>

          {product.isActive ? (
            <Tooltip label="השבת" withArrow>
              <ActionIcon
                aria-label="השבת"
                data-testid={`product-settings-toggle-${product.id}`}
                variant="subtle"
                color="red"
                radius="md"
                size="lg"
                className={bulkClasses.statusActionIcon}
                onClick={() => onDeactivate(product.id)}
                disabled={isTogglingProductStatus}
              >
                <FontAwesomeIcon icon={faBan} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Tooltip label="הפעל" withArrow>
              <ActionIcon
                aria-label="הפעל"
                data-testid={`product-settings-toggle-${product.id}`}
                variant="subtle"
                color="green"
                radius="md"
                size="lg"
                className={bulkClasses.statusActionIcon}
                onClick={() => onActivate(product.id)}
                disabled={isTogglingProductStatus}
              >
                <FontAwesomeIcon icon={faCircleCheck} />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      )}
    </td>
  </tr>
);

export const ProductSettingsRow = React.memo(ProductSettingsRowComponent);
